import { Dna } from 'entities/dna'
import { PcrProtocol } from 'entities/pcr_protocol'
import { PcrRun, PcrRunProbes, PROBE_COLORS } from 'entities/pcr_run'
import { Probe } from 'entities/probe'
import moment from 'moment'
import { Badge } from 'react-bootstrap'
import { ALPHABET, hex2rgb, downloadFile } from 'utils/util'
import { ViewModel } from 'view_models/view_model'

export type PcrCell = {
    dna?: Dna | string
    probe?: Probe
}

export class PcrRunViewModel extends ViewModel<PcrRun> {
    public statusView = (): JSX.Element | undefined => {
        switch (this.object.status) {
            case 'NOT_STARTED':
                return <Badge bg="secondary">未実行</Badge>
            case 'RERUN':
                return <Badge bg="warning">再測定</Badge>
            case 'FAILURE':
                return <Badge bg="danger">QCエラー</Badge>
            case 'COMPLETED':
                return <Badge bg="success">完了済</Badge>
        }
    }
    public dnaColumnCount = () => {
        const dnaCount = this.object.pcrProtocol?.sampleCount
        if (!dnaCount) {
            return 0
        }
        return Math.max(dnaCount / 8, 2)
    }

    public createWellArray = (): Array<undefined | Probe | Dna | string>[] => {
        const wellArray: Array<undefined | Probe | Dna | string>[] = new Array(8).fill(0).map((_, index) => new Array(12).fill(undefined))
        const protocol = this.object.pcrProtocol
        if (!protocol) {
            return wellArray
        }
        if (protocol.version === 1) {
            return this.createWellArrayV1(wellArray, protocol)
        } else if (protocol.version === 2) {
            return this.createWellArrayV2(wellArray, protocol)
        } else {
            return wellArray
        }
    }

    public createWellArrayV1 = (wellArray: Array<undefined | Probe | Dna | string>[], protocol: PcrProtocol): Array<undefined | Probe | Dna | string>[] => {
        // プローブの追加
        if (protocol.probeCount! < 12 && protocol.sampleCount! !== 8) {
            // プローブは2列にならないパターン
            new Array(8).fill(0).map((_, row) => {
                this.object.pcrRunProbes?.forEach((prp, col) => {
                    const probe = prp.probe!
                    wellArray[row][col] = probe
                })
            })
        } else {
            let probeIndex = 0
            new Array(12).fill(0).map((_, col) => {
                new Array(2).fill(0).map((_, v) => {
                    new Array(4).fill(0).map((_, row) => {
                        const addRowValue = v === 0 ? 0 : 4
                        const prp = this.object.pcrRunProbes![probeIndex]
                        if (prp) {
                            wellArray[row + addRowValue][col] = prp.probe
                        }
                    })
                    probeIndex += 1
                })
            })
        }
        // }

        const dnaColCount = this.dnaColumnCount()
        let dnaCount = 0
        const startIndex = 12 - dnaColCount
        new Array(8).fill(0).map((_, index) => {
            for (let i = startIndex; i < 12; i++) {
                let dna: Dna | string | undefined
                if (this.object.dnaSet?.length && this.object.dnaSet.length + 1 >= dnaCount) {
                    dna = this.object.dnaSet![dnaCount]
                    if (!dna && dnaCount === this.object.dnaSet!.length) {
                        dna = 'qPCR_standard'
                    } else if (!dna && dnaCount === this.object.dnaSet!.length + 1) {
                        dna = undefined
                    }
                }
                wellArray[index][i] = dna
                if (7 === dnaCount && this.object.dnaSet!.length <= 8 && this.object.pcrProtocol?.sampleCount === 8) {
                    dnaCount = 0
                } else {
                    dnaCount += 1
                }
            }
        })

        if (protocol.sampleCount === 8 && this.object.dnaSet!.length + 1 < 7) {
            const newList = wellArray.map((row) => {
                if (!row[row.length - dnaColCount]) {
                    return [...new Array(10).fill(0).map((_) => undefined), row[10], row[11]]
                } else {
                    return row
                }
            })
            return newList
        } else {
            return wellArray
        }
    }

    public sortedPcrProbes = (): PcrRunProbes[] => {
        return (
            this.object.pcrRunProbes?.sort((a, b) => {
                if ((a.probe!.isQc && b.probe!.isQc) || (!a.probe!.isQc && !b.probe!.isQc)) {
                    if (a.probe!.name! < b.probe!.name!) {
                        return 1
                    } else if (a.probe!.name! > b.probe!.name!) {
                        return -1
                    } else {
                        return 0
                    }
                } else if (a.probe!.isQc) {
                    return -1
                } else {
                    return 1
                }
            }) || []
        )
    }

    public sortedDnaSet = (): Dna[] => {
        return this.object.dnaSet?.sort((a, b) => (a.code! < b.code! ? 1 : a.code! > b.code! ? -1 : 0)) || []
    }

    public createWellArrayV2 = (wellArray: Array<undefined | Probe | Dna | string>[], protocol: PcrProtocol): Array<undefined | Probe | Dna | string>[] => {
        let probeIndex = 0
        const probeColCount = Math.ceil(protocol.probeCount! / 8)
        for (let col = 0; col < probeColCount; col++) {
            for (let row = 0; row < 8; row++) {
                if (probeIndex == this.object.pcrRunProbes!.length) {
                    break
                }
                const sortedProbes = this.sortedPcrProbes().map((prp) => prp.probe!)
                const probe = sortedProbes[probeIndex]
                wellArray[row][col] = probe
                probeIndex++
            }
        }

        const qcProbes = this.object
            .pcrRunProbes!.map((prp) => prp.probe!)
            .filter((p) => p.isQc)
            .sort((a, b) => (a.name! < b.name! ? 1 : a.name! > b.name! ? -1 : 0))
        qcProbes.forEach((probe, row) => {
            wellArray[row][probeColCount] = probe
            wellArray[row][probeColCount + 1] = 'qPCR_standard'
        })

        const dnaStartCol = probeColCount + 2
        this.sortedDnaSet()
            .reverse()
            .forEach((dna, index) => {
                for (let row = 0; row < 8; row++) {
                    wellArray[row][12 - index - 1] = dna
                }
            })

        return wellArray
    }

    public convertPos2Str = (x: number, y: number): string => {
        const alphabet = ALPHABET[y - 1]
        return alphabet + x
    }

    public convertStr2Pos = (str_position: string): number[] => {
        const alphabet = str_position[0]
        const x = Number(str_position.substring(1))
        const y = ALPHABET.indexOf(alphabet)
        return [x - 1, y]
    }

    public create384Array = (wellArray: Array<undefined | Probe | Dna | string>[]): Array<PcrCell>[] => {
        const protocol = this.object.pcrProtocol
        const array: Array<PcrCell>[] = new Array(16).fill(0).map((_) => new Array(24).fill({}))
        if (!protocol || !protocol.commands) {
            return array
        }

        const commands: string[][] = JSON.parse(protocol.commands)
        const x = 0
        const y = 1
        commands.map((command) => {
            const from = this.convertStr2Pos(command[0])
            const to = this.convertStr2Pos(command[1])
            const aspirate = wellArray[from[y]][from[x]]
            if (!aspirate) {
                return
            }

            if ((aspirate as Dna).code) {
                array[to[y]][to[x]] = { ...array[to[y]][to[x]], dna: aspirate as Dna }
            } else if ((aspirate as Probe).name) {
                array[to[y]][to[x]] = { ...array[to[y]][to[x]], probe: aspirate as Probe }
            } else if (typeof aspirate === 'string') {
                array[to[y]][to[x]] = { ...array[to[y]][to[x]], dna: aspirate as string }
            }
        })
        return array
    }

    public getDnaNumber = (dna: Dna) => {
        const index = this.sortedDnaSet().findIndex((d) => d.id === dna.id)
        if (index >= 0) {
            return index + 1
        } else {
            return 0
        }
    }

    public getProbeNumber = (probe: Probe) => {
        const index = this.sortedPcrProbes().findIndex((p) => p.probe?.id === probe.id)
        if (index >= 0) {
            return index + 1
        } else {
            return 0
        }
    }

    public getDnaColor = (dna: Dna) => {
        const index = this.sortedDnaSet().findIndex((d) => d.id === dna.id)
        if (index >= 0) {
            return PROBE_COLORS[index % PROBE_COLORS.length]
        }
    }

    public getProbeColor = (probe: Probe) => {
        const index = this.sortedPcrProbes().findIndex((p) => p.probe?.id === probe.id)
        if (index >= 0) {
            return PROBE_COLORS[index % PROBE_COLORS.length]
        }
    }

    public downloadRunSettingFile = () => {
        let text = `* Block Type = 384-Well Block
* Chemistry = TAQMAN
* Date Created = ${moment().format('YYYY-MM-DD hh:mm:ss A')} JST
* Experiment Barcode = 
* Experiment Comment = NA
* Experiment File Name = 
* Experiment Name = ${this.object.runId}
* Experiment Run End Time = Not Started
* Experiment Type = Comparative Cт (ΔΔCт) 
* Instrument Type = QuantStudio™ 5 System
* Passive Reference = ROX
* Post-read Stage/Step = 
* Pre-read Stage/Step = 
* Quantification Cycle Method = Ct
* Signal Smoothing On = true
* Stage/ Cycle where Ct Analysis is performed = Stage2, Step2
* User Name = NA

[Sample Setup]
Well	Well Position	Sample Name	Sample Color	Biogroup Name	Biogroup Color	Target Name	Target Color	Task	Reporter	Quencher	Quantity	Comments`
        const array384 = this.create384Array(this.createWellArray())
        let wellCount = 0
        const rowSize = 16
        const colSize = 24
        new Array(rowSize).fill(0).map((_, row) => {
            new Array(colSize).fill(0).map((_, col) => {
                const cell = array384[row][col]
                const dnaId = typeof cell.dna === 'string' ? cell.dna : cell.dna?.code
                const sampleColor = typeof cell.dna !== 'string' && cell.dna ? this.getDnaColor(cell.dna) : ''
                const rgbSampleColor = sampleColor ? `"RGB(${hex2rgb(sampleColor).join(',')})"` : '"RGB(0,0,0)"'
                const probeName = cell.probe ? cell.probe.name : undefined
                const probeColor = cell.probe ? this.getProbeColor(cell.probe)! : ''
                const rgbProbeColor = sampleColor ? `"RGB(${hex2rgb(probeColor).join(',')})"` : '"RGB(0,0,0)"'
                wellCount += 1
                if (!dnaId || !probeName) {
                    // undefinedの時のみ
                    text += `\n${wellCount}\t${this.convertPos2Str(col + 1, row + 1)}`
                } else {
                    text += `\n${wellCount}\t${this.convertPos2Str(col + 1, row + 1)}\t${dnaId}\t${rgbSampleColor}\t\t\t${probeName}\t${rgbProbeColor}\t${
                        dnaId === 'Blank' ? 'NTC' : 'UNKNOWN'
                    }\tFAM\tNFQ-MGB\t\t`
                }
            })
        })
        downloadFile(text, this.object.runId! + '.txt', 'text/plane')
    }

    public create96Array = (): Array<PcrCell>[] => {
        const rowSize = 8
        const colSize = 12
        const array: Array<PcrCell>[] = new Array(rowSize).fill(0).map((_) => new Array(colSize).fill({}))

        const replicationCount = 2

        let rowIndex = 0
        let colIndex = 0
        const list = [...this.object.dnaSet!, 'Blank'] // Blankをundefinedとして追加する
        const minLength = Math.min(list.length, this.object.pcrRunProbes!.length)
        const maxLength = Math.max(list.length, this.object.pcrRunProbes!.length)
        let direction = 'none'
        if (minLength <= rowSize / replicationCount && maxLength <= colSize) {
            direction = 'horizontal'
        } else if (minLength <= colSize / replicationCount && maxLength <= rowSize) {
            direction = 'vertical'
        } else if (minLength <= (rowSize / replicationCount) * 2 && maxLength <= colSize / 2) {
            direction = 'horizontal'
        } else if (minLength <= (colSize / replicationCount) * 2 && maxLength <= rowSize / 2) {
            direction = 'vertical'
        }

        const isLongDna = list.length > this.object.pcrRunProbes!.length
        const arrays = isLongDna ? [this.object.pcrRunProbes!.map((prp) => prp.probe!), list] : [list, this.object.pcrRunProbes!.map((prp) => prp.probe!)]

        arrays[0].forEach((data1) => {
            arrays[1].forEach((data2) => {
                new Array(replicationCount).fill(0).map((_, index) => {
                    if (direction === 'horizontal') {
                        if (colSize < colIndex + 1) {
                            rowIndex += 1
                            colIndex = 0
                        }
                    } else {
                        if (rowSize < rowIndex + 1) {
                            colIndex += 1
                            rowIndex = 0
                        }
                    }
                    if (isLongDna) {
                        array[rowIndex][colIndex] = { probe: data1 as Probe, dna: data2 }
                    } else {
                        array[rowIndex][colIndex] = { probe: data2 as Probe, dna: data1 }
                    }
                    if (direction === 'horizontal') {
                        colIndex += 1
                    } else {
                        rowIndex += 1
                    }
                })
            })
            if (direction !== 'none') {
                if (direction === 'horizontal') {
                    colIndex = 0
                    rowIndex += 1
                } else {
                    rowIndex = 0
                    colIndex += 1
                }
            }
        })
        return array
    }

    public downloadRunSettingFile96 = () => {
        let text = `* Block Type = 96-Well Block
* Chemistry = TAQMAN
* Date Created = ${moment().format('YYYY-MM-DD hh:mm:ss A')} JST
* Experiment Barcode = 
* Experiment Comment = NA
* Experiment File Name = 
* Experiment Name = ${this.object.runId}
* Experiment Run End Time = Not Started
* Experiment Type = Comparative Cт (ΔΔCт) 
* Instrument Type = QuantStudio™ 5 System
* Passive Reference = ROX
* Post-read Stage/Step = 
* Pre-read Stage/Step = 
* Quantification Cycle Method = Ct
* Signal Smoothing On = true
* Stage/ Cycle where Ct Analysis is performed = Stage2, Step2
* User Name = NA

[Sample Setup]
Well	Well Position	Sample Name	Sample Color	Biogroup Name	Biogroup Color	Target Name	Target Color	Task	Reporter	Quencher	Quantity	Comments`
        const array96 = this.create96Array()
        let wellCount = 0
        const rowSize = 8
        const colSize = 12
        new Array(rowSize).fill(0).map((_, row) => {
            new Array(colSize).fill(0).map((_, col) => {
                const cell = array96[row][col]
                const dnaId = typeof cell.dna === 'string' ? cell.dna : cell.dna?.code
                const sampleColor = typeof cell.dna !== 'string' && cell.dna ? this.getDnaColor(cell.dna) : ''
                const rgbSampleColor = sampleColor ? `"RGB(${hex2rgb(sampleColor).join(',')})"` : '"RGB(0,0,0)"'
                const probeName = cell.probe ? cell.probe.name : undefined
                const probeColor = cell.probe ? this.getProbeColor(cell.probe)! : ''
                const rgbProbeColor = sampleColor ? `"RGB(${hex2rgb(probeColor).join(',')})"` : '"RGB(0,0,0)"'
                wellCount += 1
                if (!dnaId || !probeName) {
                    text += `\n${wellCount}\t${this.convertPos2Str(col + 1, row + 1)}`
                } else {
                    text += `\n${wellCount}\t${this.convertPos2Str(col + 1, row + 1)}\t${dnaId}\t${rgbSampleColor}\t\t\t${probeName}\t${rgbProbeColor}\t${
                        dnaId === 'Blank' ? 'NTC' : 'UNKNOWN'
                    }\tFAM\tNFQ-MGB\t\t`
                }
            })
        })
        downloadFile(text, this.object.runId! + '.txt', 'text/plane')
    }
}
